import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FileDown, Loader2, Printer, X } from 'lucide-react';
import { reportsAPI, prescriptionAPI } from '../../../services/api';
import useDialog from '../../../hooks/useDialog';
import usePdfDownload from '../../../hooks/usePdfDownload';
import { fmtMoney, fmtNum } from './chartTheme';
import { fmtDate, fmtDateTime } from './profileKit';
import { useScrollToTopOnChange } from '../../../utils/scroll';
import TablePager, { usePagedRows } from '../../TablePager';
import PrescriptionDetailModal from '../../PrescriptionDetailModal';
import { useToast } from '../../UIProvider';
import { onActivateKey, ROW_FOCUS_RING } from '../../../utils/keyboard';
import { printReport, saveReportPdf } from './printReport';

// The detail behind a report row. Every report opens this same component; what it shows comes
// entirely from the backend envelope ({ title, subtitle, fields, kpis, tables, note }), so a new
// drill is a backend change and every report's detail view stays visually identical.
//
// A table may declare `filters` ([{ key, label }]) that its rows answer to through their `tags`;
// they show as chips over the table, each with its count, and a filter no row answers to is not
// offered. Print lays the detail out on the report letterhead, as filtered, with the envelope's
// optional `printTitle` and `signatures` (sign-off lines for a copy that is passed on).
//
// Flat emerald header, rounded-2xl, bg-black/60 scrim, no backdrop blur, explicit X and Close.

const STATUS_TONES = [
  [/^(active|completed|paid|issued|prescribed|in.?stock|same|own cnic|received)$/i, 'bg-emerald-100 text-emerald-700'],
  [/^(cancelled|expired|blacklisted|differs|out.?of.?stock|refunded|suspended|deceased)$/i, 'bg-rose-100 text-rose-700'],
  [/^(pending|draft|initiated|low.?stock|urgent|partial|deferred|critical)$/i, 'bg-amber-100 text-amber-800'],
  [/^(guardian cnic|dispense|revisit|referred)$/i, 'bg-sky-100 text-sky-700'],
];

const toneFor = (value) => {
  const s = String(value ?? '').trim();
  const hit = STATUS_TONES.find(([re]) => re.test(s));
  return hit ? hit[1] : 'bg-gray-100 text-gray-600';
};

// COMPLETED -> Completed, READY_FOR_DOCTOR -> Ready for doctor.
const human = (value) => {
  const s = String(value ?? '').trim();
  if (!s) return '';
  if (s !== s.toUpperCase()) return s;
  const lower = s.replace(/_/g, ' ').toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
};

const isBlank = (v) => v === null || v === undefined || String(v).trim() === '';

const renderValue = (value, type) => {
  if (isBlank(value)) return <span className="text-gray-300">—</span>;
  switch (type) {
    case 'money':
      return fmtMoney(value);
    case 'number':
      return fmtNum(value);
    case 'date':
      return fmtDate(value);
    case 'datetime':
      return fmtDateTime(value);
    case 'mono':
      return <span className="font-mono text-xs">{value}</span>;
    case 'status':
      return <span className={`px-2 py-0.5 rounded-md text-xs font-medium whitespace-nowrap ${toneFor(value)}`}>{human(value)}</span>;
    default:
      return String(value);
  }
};

const alignOf = (type) => (type === 'money' || type === 'number' ? 'text-right' : 'text-left');

// A value as the printed copy writes it: the screen's own formatting, as plain text.
const textOf = (value, type) => {
  if (isBlank(value)) return '—';
  switch (type) {
    case 'money':
      return fmtMoney(value);
    case 'number':
      return fmtNum(value);
    case 'status':
      return human(value);
    case 'date':
      return fmtDate(value);
    case 'datetime':
      return fmtDateTime(value);
    default:
      return String(value);
  }
};

const PRINT_NOWRAP_CHARS = 24;

const countOf = (rows, key) => rows.filter((r) => (r.tags || []).includes(key)).length;

// The filter a table is showing: the one chosen, while any row still answers to it.
const activeFilter = (table, chosen) => {
  const rows = table.rows || [];
  return chosen && (table.filters || []).some((f) => f.key === chosen) && countOf(rows, chosen) > 0 ? chosen : null;
};

const rowsFor = (table, key) => (key ? (table.rows || []).filter((r) => (r.tags || []).includes(key)) : table.rows || []);

const FilterChips = ({ table, active, onChange }) => {
  const rows = table.rows || [];
  const offered = (table.filters || [])
    .map((f) => ({ ...f, count: countOf(rows, f.key) }))
    .filter((f) => f.count > 0);
  if (offered.length === 0) return null;
  const chips = [{ key: null, label: 'All', count: rows.length }, ...offered];
  return (
    <div role="group" aria-label={`Show in ${table.title}`} className="flex flex-wrap items-center gap-1.5">
      {chips.map((c) => {
        const on = active === c.key;
        return (
          <button
            key={c.key || 'all'}
            type="button"
            aria-pressed={on}
            onClick={() => onChange(c.key)}
            className={`px-2.5 py-1 rounded-lg text-xs font-medium whitespace-nowrap transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 ${
              on ? 'bg-navy-800 text-white' : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-100'
            }`}
          >
            {c.label}
            <span className={`ml-1.5 tabular-nums ${on ? 'text-white/80' : 'text-gray-400'}`}>{c.count}</span>
          </button>
        );
      })}
    </div>
  );
};

// A row the server sent a `prescriptionId` with (a visit or a prescription, and only to a reader
// who may open one) opens that prescription. Rows without one stay plain.
const DetailTable = ({ table, filter, onFilter, onOpenPrescription, openingId }) => {
  const all = table.rows || [];
  const active = activeFilter(table, filter);
  const rows = useMemo(() => rowsFor(table, active), [table, active]);
  // A drill can list a patient's every visit or a medicine's every movement, so it pages like the
  // report tables do. The size is remembered once for every drill table, which share one layout.
  const paging = usePagedRows(rows, { id: 'report.drill', resetOn: [active] });
  const scrollRef = useRef(null);
  useScrollToTopOnChange(scrollRef, [paging.page, paging.pageSize]);
  return (
    <section>
      <h4 className="text-sm font-semibold text-gray-900 mb-2">
        {table.title}
        {all.length > 0 && <span className="ml-2 text-xs font-normal text-gray-400">{all.length}</span>}
      </h4>
      <div className="border border-gray-200 rounded-xl overflow-hidden">
        <TablePager
          paging={paging}
          className="px-3"
          filteredFrom={active ? all.length : undefined}
          toolbar={<FilterChips table={table} active={active} onChange={onFilter} />}
        />
        {/* Its own scroller with the header pinned, so a long, wide list scrolls across without
            first scrolling the dialog to its last row. */}
        <div ref={scrollRef} className="overflow-auto max-h-[60vh]">
          <table className="w-full">
            <thead>
              <tr>
                {table.columns.map((c) => (
                  <th
                    key={c.key}
                    className={`sticky top-0 z-10 bg-gray-50 shadow-[inset_0_-1px_0_#e5e7eb] px-3 py-2 text-[11px] font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap ${alignOf(c.type)}`}
                  >
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={table.columns.length} className="px-3 py-8 text-center text-sm text-gray-400">
                    {table.emptyText || 'Nothing to show'}
                  </td>
                </tr>
              ) : (
                paging.pageRows.map((row, i) => {
                  const rxId = onOpenPrescription ? row.prescriptionId : null;
                  const open = rxId ? () => onOpenPrescription(rxId) : undefined;
                  return (
                  <tr
                    key={i}
                    tabIndex={rxId ? 0 : undefined}
                    onClick={open}
                    onKeyDown={rxId ? onActivateKey(open) : undefined}
                    title={rxId ? 'Open the prescription' : undefined}
                    aria-busy={rxId && openingId === rxId ? true : undefined}
                    className={`hover:bg-gray-50 transition-colors ${rxId ? `cursor-pointer ${ROW_FOCUS_RING}` : ''} ${
                      rxId && openingId === rxId ? 'opacity-60' : ''
                    }`}
                  >
                    {table.columns.map((c) => (
                      <td
                        key={c.key}
                        className={`px-3 py-2 text-sm text-gray-700 whitespace-nowrap ${alignOf(c.type)}`}
                      >
                        {renderValue(row[c.key], c.type)}
                      </td>
                    ))}
                  </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <TablePager paging={paging} position="bottom" className="px-3" />
      </div>
    </section>
  );
};

const ReportDetailModal = ({ spec, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rx, setRx] = useState(null);
  const [openingRx, setOpeningRx] = useState(null);
  // The filter each table shows, by its place in the envelope.
  const [filters, setFilters] = useState({});
  const notify = useToast();

  const openPrescription = async (id) => {
    if (openingRx) return;
    setOpeningRx(id);
    try {
      setRx(await prescriptionAPI.getById(id));
    } catch (e) {
      notify('error', e?.message || 'The prescription could not be opened');
    } finally {
      setOpeningRx(null);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setFilters({});
    try {
      setData(await reportsAPI.getDrill(spec));
    } catch (e) {
      setError(e?.message || 'The details could not be loaded');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [spec]);

  useEffect(() => { load(); }, [load]);

  const detailDialogRef = useDialog(onClose, { confirmDiscard: false });

  const fields = (data?.fields || []).filter((f) => !isBlank(f.value));
  const kpis = data?.kpis || [];
  const tables = data?.tables || [];

  // The letterhead copy, as filtered: printed, or saved as a PDF.
  const printProps = () => {
    const printed = tables.map((t, i) => {
      const active = activeFilter(t, filters[i]);
      const rows = rowsFor(t, active);
      const label = active ? t.filters.find((f) => f.key === active).label : null;
      return {
        title: t.title,
        subtitle: active ? `Showing ${label}: ${rows.length} of ${(t.rows || []).length}` : undefined,
        columns: t.columns.map((c) => ({
          key: c.key,
          label: c.label,
          align: alignOf(c.type) === 'text-right' ? 'right' : undefined,
          // Short values ("10 packs", "Snare 500mg") stay on one line; the long ones wrap.
          nowrap: rows.every((r) => textOf(r[c.key], c.type).length <= PRINT_NOWRAP_CHARS),
          csv: (row) => textOf(row[c.key], c.type),
        })),
        rows,
        emptyText: t.emptyText,
      };
    });
    return {
      title: data.printTitle || data.title,
      subtitle: data.subtitle || '',
      fields: fields.map((f) => ({ label: f.label, value: textOf(f.value, f.type) })),
      kpis: kpis.map((k) => ({ label: k.label, value: textOf(k.value, k.type) })),
      tables: printed,
      footnote: data.note,
      signatures: data.signatures || [],
    };
  };
  const printDetail = () => {
    if (!printReport(printProps())) notify('warning', 'The browser blocked the print window. Allow pop-ups for this site and try again.');
  };
  const [downloading, download] = usePdfDownload();
  const saveDetail = () => download(() => saveReportPdf(printProps()));

  return (
    <div
      ref={detailDialogRef}
      className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 print:hidden"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full max-h-[90vh] flex flex-col overflow-hidden">

        <div className="flex-shrink-0 relative bg-gradient-to-r from-navy-800 to-navy-700 text-white">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-10 p-2 hover:bg-white/20 rounded-xl transition-colors"
            title="Close"
          >
            <X className="h-5 w-5 text-white" />
          </button>
          <div className="p-6 pr-16">
            <h2 className="text-2xl font-bold text-white break-words">
              {loading ? 'Loading' : data?.title || 'Details'}
            </h2>
            {!loading && data?.subtitle && (
              <p className="text-sm text-white text-opacity-90 mt-1">{data.subtitle}</p>
            )}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {loading && (
            <div className="flex items-center justify-center py-20 text-gray-400 gap-2">
              <Loader2 className="w-5 h-5 animate-spin" /> Loading details
            </div>
          )}

          {!loading && error && (
            <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-lg text-sm text-red-800">
              {error}
            </div>
          )}

          {!loading && !error && (
            <>
              {kpis.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                  {kpis.map((k, i) => (
                    <div key={i} className="bg-gray-50 rounded-xl p-3">
                      <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide truncate" title={k.label}>
                        {k.label}
                      </p>
                      {/* Text figures wrap; a truncated one hid the very thing it was there to
                          explain ("CNIC differs b..."). Numbers and money are short enough that
                          wrapping never triggers, so they are unaffected. */}
                      <p
                        className={`mt-0.5 font-bold text-gray-900 ${
                          k.type === 'text' ? 'text-sm leading-snug break-words' : 'text-lg truncate'
                        }`}
                        title={String(k.value ?? '')}
                      >
                        {renderValue(k.value, k.type)}
                      </p>
                    </div>
                  ))}
                </div>
              )}

              {fields.length > 0 && (
                <section>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-3 border border-gray-200 rounded-xl p-4">
                    {fields.map((f, i) => (
                      <div key={i} className="min-w-0">
                        <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide">{f.label}</p>
                        <p className="text-sm text-gray-900 break-words">{renderValue(f.value, f.type)}</p>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {tables.map((t, i) => (
                <DetailTable
                  key={i}
                  table={t}
                  filter={filters[i] || null}
                  onFilter={(key) => setFilters((f) => ({ ...f, [i]: key }))}
                  onOpenPrescription={openPrescription}
                  openingId={openingRx}
                />
              ))}

              {data?.note && (
                <p className="text-xs text-gray-500 border-t border-gray-100 pt-3">{data.note}</p>
              )}
            </>
          )}
        </div>

        <div className="flex-shrink-0 border-t border-gray-200 px-6 py-3 flex justify-end gap-2 bg-gray-50">
          <button
            type="button"
            onClick={saveDetail}
            disabled={loading || !!error || !data || downloading}
            className="flex items-center gap-1.5 px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            title="Save this on the hospital letterhead as a PDF, as filtered"
          >
            <FileDown className="w-4 h-4" /> {downloading ? 'Making the PDF…' : 'Download PDF'}
          </button>
          <button
            type="button"
            onClick={printDetail}
            disabled={loading || !!error || !data}
            className="flex items-center gap-1.5 px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            title="Print this on the hospital letterhead, as filtered"
          >
            <Printer className="w-4 h-4" /> Print
          </button>
          <button
            onClick={onClose}
            className="px-5 py-2.5 rounded-lg text-sm font-medium bg-emerald-600 text-white hover:bg-emerald-700"
          >
            Close
          </button>
        </div>
      </div>
      {rx && <PrescriptionDetailModal prescription={rx} onClose={() => setRx(null)} />}
    </div>
  );
};

export default ReportDetailModal;
